import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { Users, Building2, Eye, Inbox, Copy, Mail, Sparkles, Download, Shield, UserPlus, Trash2, Calendar, MessageSquare, BookOpen, CheckCircle2, X, LifeBuoy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_COLOR = {
  new: "bg-primary text-primary-foreground",
  in_progress: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-100 text-emerald-800",
};

export default function Admin() {
  const nav = useNavigate();
  const [gate, setGate] = useState(null);
  const [days, setDays] = useState("30");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [access, setAccess] = useState({ admins: [], current_user_email: "" });
  const [bookings, setBookings] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoteLoading, setPromoteLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/is-super-admin").then((r) => {
      if (!r.data.super_admin) {
        toast.error("Super admin access required");
        nav("/app");
        return;
      }
      setGate(true);
    }).catch(() => nav("/app"));
  }, [nav]);

  useEffect(() => {
    if (!gate) return;
    api.get(`/admin/analytics?days=${days}`).then((r) => setAnalytics(r.data));
  }, [gate, days]);

  useEffect(() => {
    if (!gate) return;
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/orgs").then((r) => setOrgs(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/admin/super-admins").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/library/submissions").then((r) => setSubmissions(r.data)).catch(() => {});
    api.get("/admin/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, [gate]);

  const updateContact = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    toast.success("Updated");
    const { data } = await api.get("/admin/contacts");
    setContacts(data);
  };

  const promote = async () => {
    if (!promoteEmail || !/^\S+@\S+\.\S+$/.test(promoteEmail)) {
      toast.error("Enter a valid email");
      return;
    }
    setPromoteLoading(true);
    try {
      const { data } = await api.post("/admin/super-admins", { email: promoteEmail.trim() });
      toast.success(data.already ? `${data.email} is already a super-admin (${data.already})` : `Promoted ${data.email}`);
      setPromoteEmail("");
      const { data: fresh } = await api.get("/admin/super-admins");
      setAccess(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not promote user");
    } finally {
      setPromoteLoading(false);
    }
  };

  const demote = async (email) => {
    if (!window.confirm(`Remove super-admin access for ${email}?`)) return;
    try {
      await api.delete(`/admin/super-admins/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      const { data } = await api.get("/admin/super-admins");
      setAccess(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const updateSubmissionStatus = async (id, status, note = "") => {
    try {
      await api.patch(`/admin/library/submissions/${id}`, { status, admin_note: note });
      toast.success(`Marked ${status}`);
      const { data } = await api.get("/admin/library/submissions");
      setSubmissions(data);
    } catch { toast.error("Could not update"); }
  };

  const updateTicketStatus = async (id, status) => {
    try {
      await api.patch(`/admin/tickets/${id}`, { status });
      toast.success(`Marked ${status}`);
      const { data } = await api.get("/admin/tickets");
      setTickets(data);
    } catch { toast.error("Could not update"); }
  };

  if (!gate || !analytics) return <div className="p-8 font-mono-plex text-sm">Loading admin…</div>;

  return (
    <div className="p-8 space-y-6" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">/ PLATFORM · ADMIN</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Admin Console</h1>
          <p className="text-sm text-muted-foreground mt-1">Visitors, users, orgs, support inbox. Super-admin only.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="admin-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Page views", value: analytics.page_views, icon: Eye },
          { label: "Unique sessions", value: analytics.unique_sessions, icon: Eye },
          { label: "Total users", value: analytics.totals.users, icon: Users },
          { label: "Total orgs", value: analytics.totals.orgs, icon: Building2 },
          { label: `New users (${days}d)`, value: analytics.totals.new_users_30d, icon: Users },
        ].map((k) => (
          <div key={k.label} className="border border-border bg-card p-6" data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <div className="overline text-muted-foreground">{k.label}</div>
              <k.icon className="w-4 h-4 text-primary" />
            </div>
            <div className="mt-3 font-display text-3xl font-bold">{k.value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="traffic">
        <TabsList className="border border-border bg-card rounded-sm p-1" data-testid="admin-tabs">
          <TabsTrigger value="traffic" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest">Traffic</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest">Users · {users.length}</TabsTrigger>
          <TabsTrigger value="orgs" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest">Orgs · {orgs.length}</TabsTrigger>
          <TabsTrigger value="inbox" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest">
            <Inbox className="w-3.5 h-3.5 mr-1.5" /> Inbox · {contacts.filter(c => c.status === "new").length}
          </TabsTrigger>
          <TabsTrigger value="leads" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-leads">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Leads · {leads.length}
          </TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-bookings">
            <Calendar className="w-3.5 h-3.5 mr-1.5" /> Bookings · {bookings.filter(b => b.status === "new").length}
          </TabsTrigger>
          <TabsTrigger value="submissions" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-submissions">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Library · {submissions.filter(s => s.status === "pending").length}
          </TabsTrigger>
          <TabsTrigger value="tickets" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-tickets">
            <LifeBuoy className="w-3.5 h-3.5 mr-1.5" /> Tickets · {tickets.filter(t => t.status === "open").length}
          </TabsTrigger>
          <TabsTrigger value="access" className="rounded-sm font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-access">
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Access · {access.admins.length}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="traffic" className="mt-6 space-y-4">
          <div className="border border-border bg-card p-6">
            <div className="overline text-muted-foreground mb-4">Daily page views</div>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={analytics.daily_trend}>
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717A" }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-border bg-card p-6" data-testid="top-paths">
              <div className="overline text-muted-foreground mb-4">Top pages</div>
              {analytics.top_paths.length === 0 ? <div className="text-xs text-muted-foreground">No data yet</div> :
                <table className="w-full text-sm"><tbody>
                  {analytics.top_paths.map((p) => (
                    <tr key={p.path} className="border-b border-border last:border-b-0">
                      <td className="py-2 font-mono-plex text-xs">{p.path}</td>
                      <td className="py-2 text-right font-bold">{p.count}</td>
                    </tr>
                  ))}
                </tbody></table>}
            </div>
            <div className="border border-border bg-card p-6" data-testid="top-referrers">
              <div className="overline text-muted-foreground mb-4">Top referrers</div>
              {analytics.top_referrers.length === 0 ? <div className="text-xs text-muted-foreground">No referrers yet</div> :
                <table className="w-full text-sm"><tbody>
                  {analytics.top_referrers.map((r) => (
                    <tr key={r.referrer} className="border-b border-border last:border-b-0">
                      <td className="py-2 font-mono-plex text-xs truncate max-w-xs">{r.referrer}</td>
                      <td className="py-2 text-right font-bold">{r.count}</td>
                    </tr>
                  ))}
                </tbody></table>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border"><tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Name</th>
                <th className="px-4 py-3 overline text-muted-foreground">Email</th>
                <th className="px-4 py-3 overline text-muted-foreground">Organization</th>
                <th className="px-4 py-3 overline text-muted-foreground">Role</th>
                <th className="px-4 py-3 overline text-muted-foreground">Joined</th>
                <th></th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border" data-testid={`admin-user-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.full_name}</td>
                    <td className="px-4 py-3 font-mono-plex text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.organization_name}</td>
                    <td className="px-4 py-3"><Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest">{u.role}</Badge></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <a href={`mailto:${u.email}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Email
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orgs" className="mt-6">
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border"><tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Organization</th>
                <th className="px-4 py-3 overline text-muted-foreground">Members</th>
                <th className="px-4 py-3 overline text-muted-foreground">Records</th>
                <th className="px-4 py-3 overline text-muted-foreground">Plan</th>
                <th className="px-4 py-3 overline text-muted-foreground">Status</th>
                <th className="px-4 py-3 overline text-muted-foreground">Created</th>
              </tr></thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="border-b border-border" data-testid={`admin-org-${o.id}`}>
                    <td className="px-4 py-3 font-medium">{o.name}</td>
                    <td className="px-4 py-3">{o.member_count}</td>
                    <td className="px-4 py-3">{o.record_count}</td>
                    <td className="px-4 py-3 text-xs">{o.plan || "—"}</td>
                    <td className="px-4 py-3 text-xs">{o.subscription_status || "free"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="inbox" className="mt-6 space-y-3">
          {contacts.length === 0 ? (
            <div className="border border-dashed border-border p-16 text-center bg-card">
              <Inbox className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-lg">No messages yet</p>
              <p className="text-sm text-muted-foreground mt-1">Contact form submissions land here.</p>
            </div>
          ) : contacts.map((c) => (
            <div key={c.id} className="border border-border bg-card p-5" data-testid={`admin-contact-${c.id}`}>
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium">{c.name} · <span className="font-mono-plex text-xs text-muted-foreground">{c.email}</span></div>
                  {c.company && <div className="text-xs text-muted-foreground">{c.company}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_COLOR[c.status]} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`}>
                    {c.status}
                  </Badge>
                  <span className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-sm whitespace-pre-wrap">{c.message}</p>
              <div className="mt-4 flex gap-2">
                <a href={`mailto:${c.email}?subject=Re: your message`} data-testid={`reply-${c.id}`}>
                  <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90">
                    <Mail className="w-3.5 h-3.5 mr-2" /> Reply
                  </Button>
                </a>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(c.email); toast.success("Copied"); }}>
                  <Copy className="w-3.5 h-3.5 mr-2" /> Copy
                </Button>
                {c.status !== "in_progress" && (
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => updateContact(c.id, "in_progress")} data-testid={`inprogress-${c.id}`}>
                    Mark in progress
                  </Button>
                )}
                {c.status !== "resolved" && (
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => updateContact(c.id, "resolved")} data-testid={`resolve-${c.id}`}>
                    Resolve
                  </Button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="leads" className="mt-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="overline text-accent">/ EMAIL CAPTURES</div>
              <p className="text-sm text-muted-foreground mt-1">
                Anyone who submitted their email via the lead-magnet or exit-intent modal on the landing page.
                Use for retargeting, drip email, or manual outreach.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm"
              data-testid="export-leads-csv"
              onClick={() => {
                const header = "email,source,touch_count,converted,created_at,last_seen_at\n";
                const rows = leads.map(l => [l.email, l.source || "", l.touch_count || 1, l.converted ? "yes" : "no", l.created_at || "", l.last_seen_at || ""].join(",")).join("\n");
                const blob = new Blob([header + rows], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `ruleai-leads-${new Date().toISOString().slice(0,10)}.csv`;
                a.click(); URL.revokeObjectURL(url);
                toast.success(`Exported ${leads.length} leads`);
              }}
            >
              <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground">Total leads</div>
              <div className="font-display text-2xl font-bold mt-1">{leads.length}</div>
            </div>
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground">From lead magnet</div>
              <div className="font-display text-2xl font-bold mt-1">{leads.filter(l => l.source === "lead_magnet").length}</div>
            </div>
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground">From exit intent</div>
              <div className="font-display text-2xl font-bold mt-1">{leads.filter(l => l.source === "exit_intent").length}</div>
            </div>
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground">Converted</div>
              <div className="font-display text-2xl font-bold mt-1 text-emerald-600">{leads.filter(l => l.converted).length}</div>
            </div>
          </div>

          <div className="border border-border bg-card">
            {leads.length === 0 ? (
              <div className="p-16 text-center">
                <Sparkles className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="mt-4 font-display text-lg">No leads yet</p>
                <p className="text-sm text-muted-foreground mt-1">Once visitors submit the SOP Starter Pack form or exit-intent modal, they&rsquo;ll appear here.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-border"><tr className="text-left">
                  <th className="px-4 py-3 overline text-muted-foreground">Email</th>
                  <th className="px-4 py-3 overline text-muted-foreground">Source</th>
                  <th className="px-4 py-3 overline text-muted-foreground">Touches</th>
                  <th className="px-4 py-3 overline text-muted-foreground">Captured</th>
                  <th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-border last:border-b-0" data-testid={`admin-lead-${l.id}`}>
                      <td className="px-4 py-3 font-mono-plex text-xs">{l.email}</td>
                      <td className="px-4 py-3">
                        <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-secondary text-foreground">
                          {l.source || "landing"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{l.touch_count || 1}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a href={`mailto:${l.email}?subject=Your%20RuleAI%20SOP%20Starter%20Pack`} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6 space-y-4">
          <div>
            <div className="overline text-accent">/ FOUNDER CALL REQUESTS</div>
            <p className="text-sm text-muted-foreground mt-1">
              People who clicked &ldquo;Book 15 min with Scott&rdquo; on the founder section. Highest-intent leads on the site — reply within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { s: "new",       label: "New",       tone: "text-accent" },
              { s: "contacted", label: "Contacted", tone: "text-primary" },
              { s: "scheduled", label: "Scheduled", tone: "text-blue-600" },
              { s: "completed", label: "Completed", tone: "text-emerald-600" },
              { s: "no_show",   label: "No-show",   tone: "text-muted-foreground" },
            ].map(({ s, label, tone }) => (
              <div key={s} className="border border-border bg-card p-4">
                <div className="overline text-muted-foreground">{label}</div>
                <div className={`font-display text-2xl font-bold mt-1 ${tone}`}>
                  {bookings.filter(b => b.status === s).length}
                </div>
              </div>
            ))}
          </div>

          {bookings.length === 0 ? (
            <div className="border border-border bg-card p-16 text-center">
              <Calendar className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-lg">No booking requests yet</p>
              <p className="text-sm text-muted-foreground mt-1">Requests appear here when someone clicks &ldquo;Book 15 min with Scott&rdquo; on the landing page.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="border border-border bg-card p-5" data-testid={`admin-booking-${b.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display font-bold text-lg">{b.name}</div>
                        <Badge className={`rounded-sm font-mono-plex text-[10px] uppercase tracking-widest ${
                          b.status === "new" ? "bg-accent text-white" :
                          b.status === "contacted" ? "bg-primary text-primary-foreground" :
                          b.status === "scheduled" ? "bg-blue-600 text-white" :
                          b.status === "completed" ? "bg-emerald-600 text-white" :
                          "bg-secondary text-foreground"
                        }`}>
                          {b.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                        {b.email}{b.role ? ` · ${b.role}` : ""}{b.company ? ` · ${b.company}` : ""}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-plex uppercase tracking-widest whitespace-nowrap">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : "—"}
                    </div>
                  </div>

                  {b.preferred_time && (
                    <div className="mt-3 flex items-start gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{b.preferred_time}</span>
                    </div>
                  )}

                  {b.message && (
                    <div className="mt-3 flex items-start gap-2 text-sm">
                      <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 whitespace-pre-wrap text-foreground/90 text-sm leading-relaxed">{b.message}</div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
                    <a href={`mailto:${b.email}?subject=Your%20call%20with%20Scott%20-%20RuleAI.Systems`}
                       className="text-xs text-primary hover:underline inline-flex items-center gap-1 font-mono-plex uppercase tracking-widest"
                       data-testid={`booking-mailto-${b.id}`}>
                      <Mail className="w-3 h-3" /> Reply via email
                    </a>
                    <div className="flex gap-1 flex-wrap">
                      {["contacted", "scheduled", "completed", "no_show", "closed"].map((s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant={b.status === s ? "default" : "ghost"}
                          className="rounded-sm text-[10px] font-mono-plex uppercase tracking-widest h-7 px-2"
                          onClick={() => updateBookingStatus(b.id, s)}
                          data-testid={`booking-mark-${s}-${b.id}`}
                        >
                          {s.replace("_", " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions" className="mt-6 space-y-4">
          <div>
            <div className="overline text-accent">/ LIBRARY SUBMISSIONS</div>
            <p className="text-sm text-muted-foreground mt-1">
              Templates community members have submitted. Review, approve, and publish to the public Excellence Library.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { s: "pending",   label: "Pending",   tone: "text-accent" },
              { s: "approved",  label: "Approved",  tone: "text-blue-600" },
              { s: "published", label: "Published", tone: "text-emerald-600" },
              { s: "rejected",  label: "Rejected",  tone: "text-muted-foreground" },
            ].map(({ s, label, tone }) => (
              <div key={s} className="border border-border bg-card p-4">
                <div className="overline text-muted-foreground">{label}</div>
                <div className={`font-display text-2xl font-bold mt-1 ${tone}`}>
                  {submissions.filter(x => x.status === s).length}
                </div>
              </div>
            ))}
          </div>

          {submissions.length === 0 ? (
            <div className="border border-border bg-card p-16 text-center">
              <BookOpen className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-lg">No submissions yet</p>
              <p className="text-sm text-muted-foreground mt-1">Submissions appear here when someone clicks &ldquo;Suggest a template&rdquo; on the public library.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map((s) => (
                <div key={s.id} className="border border-border bg-card p-5" data-testid={`admin-submission-${s.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display font-bold text-lg">{s.title}</div>
                        <Badge className={`rounded-sm font-mono-plex text-[10px] uppercase tracking-widest ${
                          s.status === "pending" ? "bg-accent text-white" :
                          s.status === "approved" ? "bg-blue-600 text-white" :
                          s.status === "published" ? "bg-emerald-600 text-white" :
                          "bg-secondary text-foreground"
                        }`}>{s.status}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                        {s.category} · {s.resource_type}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-plex uppercase tracking-widest whitespace-nowrap">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-foreground/90 leading-relaxed">{s.description}</p>
                  {s.why_useful && (
                    <div className="mt-3 border-l-2 border-primary pl-3 text-xs text-muted-foreground italic">
                      Why useful: {s.why_useful}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between flex-wrap gap-3">
                    <div className="text-xs text-muted-foreground">
                      <div><strong className="text-foreground">{s.submitter_name || "(anon)"}</strong> · {s.submitter_email}{s.submitter_company ? ` · ${s.submitter_company}` : ""}</div>
                      {s.external_url && <a href={s.external_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.external_url}</a>}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {["approved", "published", "rejected"].map((st) => (
                        <Button
                          key={st}
                          size="sm"
                          variant={s.status === st ? "default" : "ghost"}
                          className={`rounded-sm text-[10px] font-mono-plex uppercase tracking-widest h-7 px-2 ${
                            st === "approved" ? "hover:text-blue-600" :
                            st === "published" ? "hover:text-emerald-600" :
                            "hover:text-destructive"
                          }`}
                          onClick={() => updateSubmissionStatus(s.id, st)}
                          data-testid={`submission-mark-${st}-${s.id}`}
                        >
                          {st === "approved" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                          {st === "rejected" && <X className="w-3 h-3 mr-1" />}
                          {st}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tickets" className="mt-6 space-y-4">
          <div>
            <div className="overline text-accent">/ SUPPORT TICKETS</div>
            <p className="text-sm text-muted-foreground mt-1">
              Tickets submitted from the in-app support widget. Public support email: <a href="mailto:support@ruleai.systems" className="text-primary hover:underline">support@ruleai.systems</a>.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { s: "open",         label: "Open",         tone: "text-accent" },
              { s: "in_progress",  label: "In progress",  tone: "text-primary" },
              { s: "resolved",     label: "Resolved",     tone: "text-emerald-600" },
              { s: "closed",       label: "Closed",       tone: "text-muted-foreground" },
            ].map(({ s, label, tone }) => (
              <div key={s} className="border border-border bg-card p-4">
                <div className="overline text-muted-foreground">{label}</div>
                <div className={`font-display text-2xl font-bold mt-1 ${tone}`}>
                  {tickets.filter(t => t.status === s).length}
                </div>
              </div>
            ))}
          </div>

          {tickets.length === 0 ? (
            <div className="border border-border bg-card p-16 text-center">
              <LifeBuoy className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-lg">No tickets yet</p>
              <p className="text-sm text-muted-foreground mt-1">Tickets appear here when users click Support in the app or email {`support@ruleai.systems`}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((t) => (
                <div key={t.id} className="border border-border bg-card p-5" data-testid={`admin-ticket-${t.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-lg">{t.subject}</div>
                        <Badge className={`rounded-sm font-mono-plex text-[10px] uppercase tracking-widest ${
                          t.status === "open" ? "bg-accent text-white" :
                          t.status === "in_progress" ? "bg-primary text-primary-foreground" :
                          t.status === "resolved" ? "bg-emerald-600 text-white" :
                          "bg-secondary"
                        }`}>{t.status.replace("_", " ")}</Badge>
                        <Badge className={`rounded-sm font-mono-plex text-[10px] uppercase tracking-widest ${
                          t.priority === "urgent" ? "bg-red-600 text-white" :
                          t.priority === "high" ? "bg-orange-500 text-white" :
                          "bg-secondary text-foreground"
                        }`}>{t.priority}</Badge>
                        <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-secondary text-foreground">
                          {(t.category || "general").replace("_", " ")}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                        {t.user_name || "(anon)"} · {t.user_email}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-plex uppercase tracking-widest whitespace-nowrap">
                      {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{t.message}</p>
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
                    <a href={`mailto:${t.user_email}?subject=Re:%20${encodeURIComponent(t.subject)}`}
                       className="text-xs text-primary hover:underline inline-flex items-center gap-1 font-mono-plex uppercase tracking-widest"
                       data-testid={`ticket-mailto-${t.id}`}>
                      <Mail className="w-3 h-3" /> Reply via email
                    </a>
                    <div className="flex gap-1 flex-wrap">
                      {["in_progress", "resolved", "closed"].map((s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant={t.status === s ? "default" : "ghost"}
                          className="rounded-sm text-[10px] font-mono-plex uppercase tracking-widest h-7 px-2"
                          onClick={() => updateTicketStatus(t.id, s)}
                          data-testid={`ticket-mark-${s}-${t.id}`}
                        >
                          {s.replace("_", " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="access" className="mt-6 space-y-6">
          <div className="border border-border bg-card p-6" data-testid="access-section">
            <div className="overline text-primary">/ SUPER-ADMIN ACCESS</div>
            <h2 className="font-display font-bold text-2xl tracking-tight mt-2">Who can see this console?</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Super-admins can view every user, org, page-view, contact submission, and lead across the whole platform.
              Promote by email — the user must have registered an account first.
            </p>

            <div className="mt-6 flex gap-2 max-w-xl">
              <Input
                type="email"
                placeholder="teammate@company.com"
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") promote(); }}
                className="rounded-sm h-10"
                data-testid="promote-email-input"
              />
              <Button
                onClick={promote}
                disabled={promoteLoading}
                className="rounded-sm bg-primary hover:bg-primary/90 h-10 px-4"
                data-testid="promote-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> {promoteLoading ? "Promoting…" : "Promote"}
              </Button>
            </div>
          </div>

          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border overline text-muted-foreground">
              Current super-admins · {access.admins.length}
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-3 overline text-muted-foreground">Email</th>
                  <th className="px-4 py-3 overline text-muted-foreground">Name</th>
                  <th className="px-4 py-3 overline text-muted-foreground">Source</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {access.admins.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-muted-foreground">No super-admins configured yet.</td></tr>
                ) : access.admins.map((a) => (
                  <tr key={a.email} className="border-b border-border last:border-b-0" data-testid={`access-row-${a.email}`}>
                    <td className="px-4 py-3 font-mono-plex text-xs">
                      {a.email}
                      {a.email.toLowerCase() === access.current_user_email.toLowerCase() && (
                        <span className="ml-2 text-[10px] font-mono-plex uppercase tracking-widest text-primary">· you</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{a.full_name || "—"}</td>
                    <td className="px-4 py-3">
                      {a.source === "env" ? (
                        <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 hover:bg-amber-100">
                          <Shield className="w-3 h-3 mr-1" /> Env
                        </Badge>
                      ) : (
                        <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-primary text-primary-foreground">
                          DB
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {a.removable && a.email.toLowerCase() !== access.current_user_email.toLowerCase() ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => demote(a.email)}
                          data-testid={`demote-${a.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground font-mono-plex uppercase tracking-widest">
                          {a.source === "env" ? "Edit .env to remove" : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-muted-foreground font-mono-plex uppercase tracking-widest">
            ◉ Env admins are bootstrapped from <code className="text-foreground">SUPER_ADMIN_EMAILS</code> in <code className="text-foreground">backend/.env</code>. DB admins were promoted through this panel.
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
